import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import './styles/index.css';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { CollectionProvider } from './contexts/CollectionContext';
import { ComparisonProvider } from './contexts/ComparisonContext';

// Configure axios defaults
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

// Global Axios Interceptors for Auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if the error is 401 Unauthorized and we haven't already retried
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      
      // Don't intercept refresh token loops or login endpoints
      if (originalRequest.url.includes('/api/auth/refresh') || originalRequest.url.includes('/api/auth/login')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post('/api/auth/refresh', {}, { withCredentials: true });
        
        const newAccessToken = data.access_token;
        localStorage.setItem('access_token', newAccessToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        
        processQueue(null, newAccessToken);
        return axios(originalRequest);
        
      } catch (err) {
        processQueue(err, null);
        console.warn('Session expired or invalid token. Redirecting to login.');
        localStorage.removeItem('access_token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <AuthProvider>
      <CollectionProvider>
        <ComparisonProvider>
          <WebSocketProvider>
            <App />
          </WebSocketProvider>
        </ComparisonProvider>
      </CollectionProvider>
    </AuthProvider>
  </React.StrictMode>
);
